import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { arrayMove } from '@dnd-kit/sortable'

import { useFetch } from '@reveni-io/reveni-ui'

import { pageSize } from './constants'
import { transformRequestReorderAvailabilityCriteria, transformResponseAvailabilityCriteria } from './transformers'

const useAvailabilityCriteria = ({
  storeId,
  setToastInfo,
  getAvailabilityCriteria,
  reorderAvailabilityCriteria,
  deleteAvailabilityCriterion,
  detailRoute,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const {
    data: availabilityCriteriaData,
    fetchData: fetchAvailabilityCriteria,
    loading: gettingAvailabilityCriteria,
  } = useFetch(getAvailabilityCriteria, {
    doInitialRequest: false,
    transformResponse: transformResponseAvailabilityCriteria,
  })

  const { fetchData: reorderAvailabilityCriteriaRequest, loading: reorderingAvailabilityCriteria } = useFetch(
    reorderAvailabilityCriteria,
    { doInitialRequest: false, transformRequest: transformRequestReorderAvailabilityCriteria }
  )

  const { fetchData: deleteAvailabilityCriterionRequest, loading: deletingAvailabilityCriterion } = useFetch(
    deleteAvailabilityCriterion,
    { doInitialRequest: false }
  )

  const [availabilityCriteria, setAvailabilityCriteria] = useState([])
  const [hasChanges, setHasChanges] = useState(false)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [criterionToDelete, setCriterionToDelete] = useState(null)
  const pageRef = useRef(1)

  useEffect(() => {
    setPage(1)
    pageRef.current = 1
  }, [search])

  useEffect(() => {
    pageRef.current = page
    fetchAvailabilityCriteria({ storeId, search, page, pageSize })
  }, [storeId, search, page, fetchAvailabilityCriteria])

  const setInitValues = useCallback(() => {
    if (availabilityCriteriaData) {
      if (pageRef.current === 1) {
        setAvailabilityCriteria(availabilityCriteriaData.results)
      } else {
        setAvailabilityCriteria(prev => [...prev, ...availabilityCriteriaData.results])
      }
    }
    setHasChanges(false)
  }, [availabilityCriteriaData])

  useEffect(() => {
    setInitValues()
  }, [setInitValues])

  const reorderCriteria = event => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    setAvailabilityCriteria(prev => {
      const oldIndex = prev.findIndex(c => c.id === active.id)
      const newIndex = prev.findIndex(c => c.id === over.id)
      return arrayMove(prev, oldIndex, newIndex)
    })
    setHasChanges(true)
  }

  const refetchFromStart = useCallback(() => {
    setPage(1)
    pageRef.current = 1
    fetchAvailabilityCriteria({ storeId, search, page: 1, pageSize })
  }, [storeId, search, fetchAvailabilityCriteria])

  const loadMore = () => {
    setPage(prev => prev + 1)
  }

  const onSave = async () => {
    try {
      await reorderAvailabilityCriteriaRequest({ storeId, criteria: availabilityCriteria })
      setToastInfo({ show: true, message: t('Cambios guardados'), type: 'success' })
      refetchFromStart()
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const revertChanges = () => {
    setInitValues()
  }

  const confirmDeleteCriterion = async () => {
    try {
      await deleteAvailabilityCriterionRequest({ storeId, criterionId: criterionToDelete.id })
      setToastInfo({ show: true, message: t('Eliminado correctamente'), type: 'success' })
      setCriterionToDelete(null)
      refetchFromStart()
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const cancelDeleteCriterion = () => {
    setCriterionToDelete(null)
  }

  const navigateToDetail = criterion => {
    navigate(detailRoute.replace(':id', criterion.id), {
      state: { childPageTitle: criterion.name, selectedCriterion: criterion },
    })
  }

  const navigateToDuplicate = criterion => {
    navigate(detailRoute.replace(':id', `duplicate-${criterion.id}`), {
      state: { selectedCriterion: criterion },
    })
  }

  const navigateToCreate = () => {
    navigate(detailRoute.replace(':id', 'new'))
  }

  return {
    availabilityCriteria,
    setAvailabilityCriteria: params => {
      setAvailabilityCriteria(params)
      setHasChanges(true)
    },
    gettingAvailabilityCriteria,
    reorderingAvailabilityCriteria,
    deletingAvailabilityCriterion,
    hasChanges,
    search,
    setSearch,
    loadMore,
    hasNextPage: !!availabilityCriteriaData?.next,
    totalCount: availabilityCriteriaData?.count || 0,
    reorderCriteria,
    onSave,
    revertChanges,
    criterionToDelete,
    setCriterionToDelete,
    confirmDeleteCriterion,
    cancelDeleteCriterion,
    navigateToDetail,
    navigateToDuplicate,
    navigateToCreate,
  }
}

export default useAvailabilityCriteria
